import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Link as MuiLink,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import EventIcon from '@mui/icons-material/Event';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import { useRouter } from 'next/router';
import { useSessionState, useSessionDispatch } from 'context/session/session';
import {
  openDataContent,
  OPEN_DATA_LICENSE_URL,
  OPEN_DATA_FOCUS_PARAM,
} from 'content/openData';

// Rôles correspondant à un référent qui gère ses propres acteurs.
const REFERENT_ROLES = ['user', 'proposeActorRole'];

const GET_MY_ADMIN_ACTORS = gql`
  query actorsAdmin($userId: String) {
    actorsAdmin(userId: $userId) {
      id
      name
      enableOpenData
      enableOpenDataAgenda
    }
  }
`;

const MARK_OPEN_DATA_PROMPT_SEEN = gql`
  mutation markOpenDataPromptSeen {
    markOpenDataPromptSeen {
      id
      openDataPromptSeenAt
    }
  }
`;

/**
 * Popup d'information OpenData, présentée UNE SEULE FOIS aux référents qui ne l'ont pas
 * encore vue (champ user.openDataPromptSeenAt). Montée globalement dans AppLayout.
 */
const OpenDataConsentDialog: React.FC = () => {
  const user = useSessionState();
  const sessionDispatch = useSessionDispatch();
  const router = useRouter();
  const [dismissed, setDismissed] = useState(false);

  const isReferent = Boolean(user?.id) && REFERENT_ROLES.includes(user?.role as string);
  const alreadySeen = Boolean(user?.openDataPromptSeenAt);
  const shouldQuery = isReferent && !alreadySeen && !dismissed;

  const { data } = useQuery(GET_MY_ADMIN_ACTORS, {
    variables: { userId: String(user?.id) },
    skip: !shouldQuery,
    fetchPolicy: 'cache-and-network',
  });

  const [markSeen] = useMutation(MARK_OPEN_DATA_PROMPT_SEEN);

  const actors = (data?.actorsAdmin || []) as Array<{
    id: string;
    name: string;
    enableOpenData?: boolean;
    enableOpenDataAgenda?: boolean;
  }>;

  // Acteur cible : en priorité un acteur dont une autorisation manque encore.
  const targetActor = useMemo(() => {
    if (!actors.length) return null;
    return (
      actors.find((a) => !a.enableOpenData || !a.enableOpenDataAgenda) || actors[0]
    );
  }, [actors]);

  const open = shouldQuery && actors.length > 0;

  const persistSeen = () => {
    setDismissed(true);
    if (user) {
      // Met à jour la session pour ne pas re-déclencher la popup pendant la navigation.
      sessionDispatch({
        type: 'login',
        payload: { ...(user as any), openDataPromptSeenAt: new Date().toISOString() },
      });
    }
    markSeen().catch(() => {});
  };

  const handleLater = () => {
    persistSeen();
  };

  const handleConfigure = () => {
    persistSeen();
    if (targetActor) {
      router.push(`/admin/actors/${targetActor.id}?focus=${OPEN_DATA_FOCUS_PARAM}`);
    }
  };

  if (!open) return null;

  return (
    <Dialog open onClose={handleLater} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
        <InfoIcon color="secondary" />
        {openDataContent.title}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {openDataContent.intro}
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <EventIcon color="secondary" />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {openDataContent.agendaLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {openDataContent.agendaHelp}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <StorefrontIcon color="secondary" />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {openDataContent.actorLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {openDataContent.actorHelp}
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'rgba(105, 182, 169, 0.12)',
            mb: 2,
          }}
        >
          <Typography variant="body2">{openDataContent.benefit}</Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {openDataContent.reassurance}{' '}
          <MuiLink href={OPEN_DATA_LICENSE_URL} target="_blank" rel="noopener noreferrer">
            {openDataContent.licenseHint}
          </MuiLink>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleLater} color="inherit">
          Plus tard
        </Button>
        <Button onClick={handleConfigure} variant="contained" color="secondary" autoFocus>
          Configurer mes autorisations
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpenDataConsentDialog;
